import type {FC} from 'react';
import * as React from "react";
import {useLayoutEffect, useState} from "react";
import {Box, CircularProgress} from "@mui/material";
import {GoalTargetStore} from "../../../../../../cog/keystone/goals";
import {useTheme} from "@mui/system";
import Chart from "react-apexcharts";
import Goal from "../../../../../../cog/keystone/goals/goal/Goal";
import {useMainContext} from "../../../../../../contexts/MainContext";
import {SVDetails} from "../../../../../../cog/keystone/goals/goal/goalTarget/SVTarget";

interface NPDChartLoaderProps {
    org?: string;
    labelValue?: string;
    goal?: Goal;
    hasDataHandler?: () => void
}

const NPDChartLoaderLoader: FC<NPDChartLoaderProps> = ({labelValue, goal, hasDataHandler, org}) => {
    const theme = useTheme();
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isOptionsLoading, setIsOptionsLoading] = useState<boolean>(true)

    const [options, setOptions] = useState<{}>()
    const [series, setSeries] = useState<any>()

    const ctx = useMainContext()

    let formatter = Intl.NumberFormat('en', {notation: 'compact'})
    const createOptions = (columns: SVDetails, yTemp?) => {
        return {
            chart: {
                type: "area",
                background: theme.palette.background.paper,
            },
            fill: {
                type: "gradient",
                gradient: {
                    shade: 'light',
                    type: 'vertical',
                    shadeIntensity: 1,
                    opacityFrom: 0.7,
                    opacityTo: 0.9,
                    stops: [0, 90, 100]
                }
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '55%',
                }
            },
            xaxis: {
                type: 'category',
                tickAmount: 10,
                labels: {
                    rotateAlways: true,
                    rotate: -60,
                    trim: true,
                    formatter: (value) => {
                        return value
                    }
                },
                tickPlacement: 'between',
                categories: columns,
            },

            theme: {
                mode: theme.palette.mode
            },
            stroke: {
                curve: 'smooth',
                colors: ["#008ffa", "#00e396", "#feb019", "#ff6178", "#008A61", "#775dd0"]
            },
            dataLabels: {
                enabled: false,
            },
            legend: {
                show: true,
                position: 'bottom',
            },
            grid: {
                padding: {
                    bottom: 20,
                    left: 80,
                }
            },
            yaxis: [...yTemp],
        }

    }
    const getGoalDetails = async () => {
        let yaxises = []
        const response = await GoalTargetStore.CalculateNPDDetails({
            clientName: ctx.activeOrganization.name,
            criteriaFilters: JSON.parse(goal.criteria),
            goalID: goal.id
        })
        if (response?.details && Object.keys(response.details).length > 0) {
            let x = Object.keys(response.details).filter((k) => k != labelValue)
            let y = Object.keys(response.details).filter((k) => k == labelValue)
            const seriesTest = x ? x.map((c) => {
                yaxises.push({
                    opposite: yaxises.length % 2 !== 0,
                    title: {
                        text: c,
                    },
                })
                return {
                    name: c,
                    data: response.details && response.details[c] ? response.details[c] : []
                }
            }) : [{name: "y", data: []}]
            seriesTest.unshift(seriesTest.pop())
            setSeries(seriesTest)
            setOptions(createOptions(response.details[labelValue], yaxises))
        } else {
            hasDataHandler()
            setSeries({name: "y", data: []})
            setOptions({
                chart: {
                    type: "line",
                }
            })
        }
    }

    useLayoutEffect(() => {
        setIsLoading(true)
        getGoalDetails().then(() => {
            setIsLoading(false)
            setIsOptionsLoading(false)
        })
    }, [theme.palette.mode])

    return (
        <Box sx={{minHeight: 500, minWidth: '300px', justifyContent: 'center'}}>
            {isLoading ? (
                <Box sx={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    display: 'flex'
                }}>
                    <CircularProgress/></Box>
            ) : isOptionsLoading ? (
                <Box sx={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    display: 'flex'
                }}>
                    <CircularProgress/>
                </Box>
            ) : (
                <Chart
                    type={'area'}
                    options={options}
                    height={"90%"}
                    width={"100%"}
                    series={series}
                    sx={{justifyContent: 'center', display: 'flex'}}
                />
            )
            }
        </Box>
    );
}

export default NPDChartLoaderLoader;